export type ContainerSize = { width: number; height: number };

export const STABLE_SIZE_FRAMES = 3;

export function readContainerSize(element: HTMLElement): ContainerSize {
  return { width: element.clientWidth, height: element.clientHeight };
}

export function hasRenderableSize(size: ContainerSize): boolean {
  return size.width > 0 && size.height > 0;
}

function isSameSize(a: ContainerSize, b: ContainerSize): boolean {
  return a.width === b.width && a.height === b.height;
}

export function waitForStableContainerSize({
  element,
  signal,
  stableFrames = STABLE_SIZE_FRAMES,
}: {
  element: HTMLElement;
  signal: AbortSignal;
  stableFrames?: number;
}): Promise<ContainerSize | null> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(null);

      return;
    }

    let frameId: number | null = null;
    let consecutiveFrames = 0;
    let lastSize: ContainerSize | null = null;
    const observer = new ResizeObserver(() => scheduleCheck());

    const finish = (size: ContainerSize | null) => {
      observer.disconnect();
      signal.removeEventListener('abort', onAbort);
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }

      resolve(size);
    };

    const onAbort = () => finish(null);

    const check = () => {
      frameId = null;
      const size = readContainerSize(element);

      if (!hasRenderableSize(size)) {
        consecutiveFrames = 0;
        lastSize = null;

        return;
      }

      if (lastSize && isSameSize(lastSize, size)) {
        consecutiveFrames += 1;
      } else {
        consecutiveFrames = 1;
        lastSize = size;
      }

      if (consecutiveFrames >= stableFrames) {
        finish(size);

        return;
      }

      frameId = requestAnimationFrame(check);
    };

    const scheduleCheck = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(check);
      }
    };

    signal.addEventListener('abort', onAbort);
    observer.observe(element);
    scheduleCheck();
  });
}

export type TerminalGeometry = { cols: number; rows: number };

export function fitTerminalToContainer({
  element,
  fit,
  current,
}: {
  element: HTMLElement;
  fit: () => void;
  current: () => TerminalGeometry;
}): { geometry: TerminalGeometry; changed: boolean } | null {
  if (!hasRenderableSize(readContainerSize(element))) {
    return null;
  }

  const before = current();

  fit();

  const geometry = current();

  return {
    geometry,
    changed: geometry.cols !== before.cols || geometry.rows !== before.rows,
  };
}
